import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getAutoSeating, batchSeatAssignment } from '@openapi/ticket-seats/ticket-seats';
import useNotify from '../../hooks/useNotify';

function itemNeedsSeat(item, ticketTypeById) {
    const ticketType = ticketTypeById[item.ticketTypeId];
    return Array.isArray(ticketType?.seats) && ticketType.seats.length > 0;
}

function computeFreedSeatIds(items) {
    const draftSeatIds = new Set(items.map((i) => i.draftTicketSeatId).filter(Boolean));
    const freed = [];
    items.forEach((item) => {
        if (item.ticketSeatId && !draftSeatIds.has(item.ticketSeatId)) {
            freed.push(item.ticketSeatId);
        }
    });
    return freed;
}

export function useSeatDraft({
    selectedGroup,
    ticketTypeById,
    elements,
    freeSeatIds,
    numericTimeslotId,
    refetchBookingItems,
    refetchFreeSeats,
    bookingItems,
    canCrossTicketType = false,
}) {
    const { t } = useTranslation();
    const { notifyError, notifySuccess, notifyInfo } = useNotify();

    // draftsByBooking: { [bookingId]: DraftItem[] }
    const [draftsByBooking, setDraftsByBooking] = useState({});
    const [suggestedSeatIds, setSuggestedSeatIds] = useState([]);
    const [isSuggesting, setIsSuggesting] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Reset all drafts when underlying data changes (after save/refetch)
    const dataVersion = useMemo(
        () => (bookingItems || []).map((i) => `${i.id}:${i.ticketSeatId ?? ''}`).join(','),
        [bookingItems],
    );

    useEffect(() => {
        setDraftsByBooking({});
        setSuggestedSeatIds([]);
    }, [dataVersion]);

    const isSeatMismatch = (item) => {
        if (!item.ticketSeatId) return false;
        const ticketType = ticketTypeById[item.ticketTypeId];
        if (!ticketType?.seats?.length) return false;
        return !ticketType.seats.some((seat) => Number(seat.id) === Number(item.ticketSeatId));
    };

    // Lazily initialize draft for selected group
    useEffect(() => {
        if (!selectedGroup) return;
        const bookingId = selectedGroup.bookingId;
        setDraftsByBooking((prev) => {
            if (prev[bookingId]) return prev;
            return {
                ...prev,
                [bookingId]: selectedGroup.items.map((item) => ({
                    ...item,
                    draftTicketSeatId: item.ticketSeatId || null,
                    crossTypeMismatch: isSeatMismatch(item),
                })),
            };
        });
        setSuggestedSeatIds([]);
    }, [selectedGroup]);

    // Current group's draft items (fall back to original items if draft not yet initialized)
    const currentDraftItems = useMemo(() => {
        if (!selectedGroup) return [];
        return (
            draftsByBooking[selectedGroup.bookingId] ||
            selectedGroup.items.map((item) => ({
                ...item,
                draftTicketSeatId: item.ticketSeatId || null,
                crossTypeMismatch: isSeatMismatch(item),
            }))
        );
    }, [draftsByBooking, selectedGroup]);

    const selectedDraftSeatIds = useMemo(
        () => currentDraftItems.map((item) => item.draftTicketSeatId).filter(Boolean),
        [currentDraftItems],
    );

    const selectedDraftSeatIdSet = useMemo(
        () => new Set(selectedDraftSeatIds),
        [selectedDraftSeatIds],
    );

    // Seats originally saved for the current group (allows re-assigning after toggling off)
    const currentGroupSavedSeatIdSet = (() => {
        if (!selectedGroup) return new Set();
        return new Set(selectedGroup.items.map((item) => item.ticketSeatId).filter(Boolean));
    })();

    // Seats assigned in OTHER groups' drafts — treated as unavailable
    const otherDraftSeatIdSet = useMemo(() => {
        const set = new Set();
        const currentBookingId = selectedGroup?.bookingId;
        Object.entries(draftsByBooking).forEach(([bookingId, items]) => {
            if (Number(bookingId) === currentBookingId) return;
            items.forEach((item) => {
                if (item.draftTicketSeatId) set.add(item.draftTicketSeatId);
            });
        });
        return set;
    }, [draftsByBooking, selectedGroup?.bookingId]);

    // Seats that were saved on the server but no longer used in OTHER groups' drafts — treat as available
    const otherDraftFreedSeatIdSet = (() => {
        const set = new Set();
        const currentBookingId = selectedGroup?.bookingId;
        Object.entries(draftsByBooking).forEach(([bookingId, items]) => {
            if (Number(bookingId) === currentBookingId) return;
            computeFreedSeatIds(items).forEach((id) => set.add(id));
        });
        return set;
    })();

    // Draft-aware missing count for any group (used in sidebar chips)
    const getDraftMissingCount = useCallback(
        (bookingId) => {
            const draft = draftsByBooking[bookingId];
            if (!draft) return null; // no draft yet — caller uses original unassignedCount
            return draft.filter(
                (item) => !item.draftTicketSeatId && itemNeedsSeat(item, ticketTypeById),
            ).length;
        },
        [draftsByBooking, ticketTypeById],
    );

    const hasCrossTypeMismatch = (bookingId) => {
        const draft = draftsByBooking[bookingId];
        if (draft) return draft.some((item) => item.crossTypeMismatch);
        // Fall back to raw booking items when draft not yet initialized
        const items = (bookingItems || []).filter((item) => item.bookingId === bookingId);
        return items.some((item) => isSeatMismatch(item));
    };

    // Total individual seat changes across all groups
    const totalChangeCount = useMemo(() => {
        let count = 0;
        Object.values(draftsByBooking).forEach((items) => {
            items.forEach((item) => {
                if ((item.draftTicketSeatId || null) !== (item.ticketSeatId || null)) count++;
            });
        });
        return count;
    }, [draftsByBooking]);

    const updateCurrentDraft = (updater) => {
        if (!selectedGroup) return;
        const bookingId = selectedGroup.bookingId;
        setDraftsByBooking((prev) => ({
            ...prev,
            [bookingId]: updater(prev[bookingId] || []),
        }));
    };

    const assignSeatToDraft = (seatId) => {
        updateCurrentDraft((prev) => {
            const existingIndex = prev.findIndex((item) => item.draftTicketSeatId === seatId);
            if (existingIndex >= 0) {
                const next = [...prev];
                next[existingIndex] = {
                    ...next[existingIndex],
                    draftTicketSeatId: null,
                    crossTypeMismatch: false,
                };
                return next;
            }

            const next = [...prev];

            // First try to find a compatible unassigned item (strict match)
            let targetIndex = next.findIndex((item) => {
                if (item.draftTicketSeatId) return false;
                const ticketType = ticketTypeById[item.ticketTypeId];
                const seatIds = (ticketType?.seats || []).map((seat) => Number(seat.id));
                return seatIds.includes(Number(seatId));
            });

            // If no compatible match and cross-type is allowed, use any unassigned item
            let isMismatch = false;
            if (targetIndex < 0 && canCrossTicketType) {
                targetIndex = next.findIndex((item) => !item.draftTicketSeatId);
                if (targetIndex >= 0) isMismatch = true;
            }

            if (targetIndex < 0) {
                notifyInfo(t('timeslots.assignment.noCompatibleTicketType'));
                return prev;
            }

            next[targetIndex] = {
                ...next[targetIndex],
                draftTicketSeatId: seatId,
                crossTypeMismatch: isMismatch,
            };
            return next;
        });
    };

    const handleSeatClick = (seatId) => {
        if (isSaving) return;
        const seatElement = elements.find((element) => element.id === seatId);
        if (!seatElement?.bookable) return;

        if (otherDraftSeatIdSet.has(seatId)) {
            notifyInfo(t('timeslots.assignment.seatUnavailable'));
            return;
        }

        const isUsedByCurrentDraft = selectedDraftSeatIdSet.has(seatId);
        const isFree = freeSeatIds.has(seatId);
        const isOwnedByCurrentGroup = currentGroupSavedSeatIdSet.has(seatId);
        const isFreedByOtherDraft = otherDraftFreedSeatIdSet.has(seatId);
        if (!isUsedByCurrentDraft && !isFree && !isOwnedByCurrentGroup && !isFreedByOtherDraft) {
            notifyInfo(t('timeslots.assignment.seatUnavailable'));
            return;
        }
        assignSeatToDraft(seatId);
    };

    const handleSuggest = async () => {
        if (!selectedGroup || currentDraftItems.length === 0) return;

        const requiredMap = new Map();
        currentDraftItems.forEach((item) => {
            if (item.draftTicketSeatId) return;
            if (!itemNeedsSeat(item, ticketTypeById)) return;
            requiredMap.set(item.ticketTypeId, (requiredMap.get(item.ticketTypeId) || 0) + 1);
        });

        const requiredSeats = Array.from(requiredMap.entries()).map(([ticketTypeId, quantity]) => ({
            ticketTypeId,
            quantity,
        }));

        if (requiredSeats.length === 0) {
            notifyInfo(t('timeslots.assignment.noMissingSeats'));
            return;
        }

        // Include seats from other drafts as occupied so auto-seating avoids them
        const otherDraftSeats = [];
        const freedSeatIds = [...otherDraftFreedSeatIdSet];
        Object.entries(draftsByBooking).forEach(([bookingId, items]) => {
            if (Number(bookingId) === selectedGroup.bookingId) return;
            items.forEach((item) => {
                if (item.draftTicketSeatId) {
                    otherDraftSeats.push({
                        id: Number(item.draftTicketSeatId),
                        ticketTypeId: Number(item.ticketTypeId),
                    });
                }
            });
        });
        // Also include seats freed from the current group
        computeFreedSeatIds(currentDraftItems).forEach((id) => freedSeatIds.push(Number(id)));

        setIsSuggesting(true);
        try {
            const response = await getAutoSeating(numericTimeslotId, {
                currentSeats: [
                    ...currentDraftItems
                        .filter((item) => item.draftTicketSeatId)
                        .map((item) => ({
                            id: Number(item.draftTicketSeatId),
                            ticketTypeId: Number(item.ticketTypeId),
                        })),
                    ...otherDraftSeats,
                ],
                requiredSeats,
                freedSeatIds,
            });

            const suggestions = response?.suggestions || [];
            const newSuggestedSeatIds = [];
            updateCurrentDraft((prev) => {
                const next = [...prev];
                suggestions.forEach((suggestion) => {
                    const openIndexes = next
                        .map((item, index) => ({ item, index }))
                        .filter(
                            ({ item }) =>
                                item.ticketTypeId === suggestion.ticketTypeId &&
                                !item.draftTicketSeatId,
                        )
                        .map(({ index }) => index);

                    suggestion.seats.forEach((seat, idx) => {
                        const targetIndex = openIndexes[idx];
                        if (targetIndex == null) return;
                        next[targetIndex] = {
                            ...next[targetIndex],
                            draftTicketSeatId: Number(seat.id),
                            crossTypeMismatch: false,
                        };
                        newSuggestedSeatIds.push(Number(seat.id));
                    });
                });
                return next;
            });
            setSuggestedSeatIds(newSuggestedSeatIds);
        } catch (error) {
            notifyError(error);
        } finally {
            setIsSuggesting(false);
        }
    };

    const handleSave = async () => {
        if (isSuggesting || totalChangeCount === 0) return;
        setIsSaving(true);
        try {
            const assignments = [];
            Object.values(draftsByBooking).forEach((items) => {
                items.forEach((item) => {
                    if ((item.draftTicketSeatId || null) !== (item.ticketSeatId || null)) {
                        assignments.push({
                            bookingItemId: item.id,
                            ticketSeatId: item.draftTicketSeatId || null,
                        });
                    }
                });
            });

            await batchSeatAssignment(numericTimeslotId, { assignments });
            notifySuccess(t('timeslots.assignment.saved'));
            setSuggestedSeatIds([]);
            await refetchBookingItems();
            await refetchFreeSeats();
        } catch (error) {
            notifyError(error);
        } finally {
            setIsSaving(false);
        }
    };

    const applyGroupDraftAssignments = useCallback(
        (assignments) => {
            // assignments: [{ bookingItemId, ticketSeatId }]
            setDraftsByBooking((prev) => {
                const next = { ...prev };
                assignments.forEach(({ bookingItemId, ticketSeatId }) => {
                    const item = (bookingItems || []).find((i) => i.id === bookingItemId);
                    if (!item) return;
                    const bookingId = item.bookingId;
                    const existing =
                        next[bookingId] ||
                        (bookingItems || [])
                            .filter((i) => i.bookingId === bookingId)
                            .map((i) => ({
                                ...i,
                                draftTicketSeatId: i.ticketSeatId || null,
                                crossTypeMismatch: false,
                            }));
                    next[bookingId] = existing.map((i) =>
                        i.id === bookingItemId
                            ? { ...i, draftTicketSeatId: ticketSeatId || null, crossTypeMismatch: false }
                            : i,
                    );
                });
                return next;
            });
        },
        [bookingItems],
    );

    const handleClearGroupSeats = useCallback(
        (bookingIds) => {
            setDraftsByBooking((prev) => {
                const next = { ...prev };
                bookingIds.forEach((bookingId) => {
                    const existing =
                        prev[bookingId] ||
                        (bookingItems || [])
                            .filter((i) => i.bookingId === bookingId)
                            .map((i) => ({ ...i, draftTicketSeatId: i.ticketSeatId || null }));
                    next[bookingId] = existing.map((i) => ({
                        ...i,
                        draftTicketSeatId: null,
                        crossTypeMismatch: false,
                    }));
                });
                return next;
            });
            setSuggestedSeatIds([]);
        },
        [bookingItems],
    );

    // Clearing one booking's seats is just the group-clear over a single id.
    const handleClearSeats = (bookingId) => handleClearGroupSeats([bookingId]);

    return {
        currentDraftItems,
        suggestedSeatIds,
        selectedDraftSeatIds,
        selectedDraftSeatIdSet,
        otherDraftSeatIdSet,
        otherDraftFreedSeatIdSet,
        currentGroupSavedSeatIdSet,
        getDraftMissingCount,
        hasCrossTypeMismatch,
        totalChangeCount,
        isSuggesting,
        isSaving,
        handleSeatClick,
        handleSuggest,
        handleSave,
        handleClearSeats,
        applyGroupDraftAssignments,
        handleClearGroupSeats,
        draftsByBooking,
    };
}
